/**
 * TeacherSessionRedirect - Redirects Supabase Auth users away from player routes
 * 
 * CRITICAL RULE: If Supabase Auth session exists, user is ALWAYS a teacher.
 * Teachers must NEVER access player routes (avatar onboarding, student dashboard, etc.)
 * 
 * This guard:
 * 1. Checks if a Supabase Auth session exists
 * 2. If yes → redirects to /join-school (if no school) or /teacher (if has school)
 * 3. If no → allows player routes to render
 * 
 * Use this to wrap player-only routes that should never be accessible to teachers.
 */

import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { Loader2 } from 'lucide-react';

/**
 * Clear all player session data from localStorage
 * This prevents conflicts when a teacher has leftover player data
 */
function clearPlayerSessionData(): void {
  try {
    // Clear new session token keys
    localStorage.removeItem('ta_session_token_v1');
    localStorage.removeItem('ta_session_expires_v1');
    localStorage.removeItem('ta_session_player_id_v1');
    // Clear old session token keys
    localStorage.removeItem('player_session_token');
    localStorage.removeItem('player_session_expires');
    // Clear any selected player/mode data
    localStorage.removeItem('selectedPlayerId');
    localStorage.removeItem('playerSession');
    localStorage.removeItem('mode');
    
    if (import.meta.env.DEV) {
      console.log('[TeacherSessionRedirect] Cleared player session data from localStorage');
    }
  } catch {
    // Ignore localStorage errors
  }
}

interface TeacherSessionRedirectProps {
  children: React.ReactNode;
}

export const TeacherSessionRedirect: React.FC<TeacherSessionRedirectProps> = ({ children }) => {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [isChecking, setIsChecking] = useState(true);
  const [shouldRenderChildren, setShouldRenderChildren] = useState(false);

  useEffect(() => {
    const checkAndRedirect = async () => {
      if (authLoading) return;

      // Demo mode: never redirect away from demo routes
      if (location.pathname.startsWith('/demo')) {
        setShouldRenderChildren(true);
        setIsChecking(false);
        return;
      }

      // Teacher game-test mode: opettaja testaa peliä omalla default-pelaajallaan
      // Sallitaan /student-reitti ilman uudelleenohjausta ja säilytetään pelaajasessio.
      if (typeof window !== 'undefined' && sessionStorage.getItem('ta_teacher_game_test_active') === 'true') {
        if (import.meta.env.DEV) {
          console.log('[TeacherSessionRedirect] Teacher game-test mode active, allowing player route');
        }
        setShouldRenderChildren(true);
        setIsChecking(false);
        return;
      }

      // No Supabase Auth session = this is a player, allow access
      if (!user) {
        if (import.meta.env.DEV) {
          console.log('[TeacherSessionRedirect] No auth session, allowing player access');
        }
        setShouldRenderChildren(true);
        setIsChecking(false);
        return;
      }

      // Supabase Auth session exists = this is a teacher
      // Teachers must NOT access player routes
      
      // CRITICAL: Clear any leftover player session data to prevent conflicts
      clearPlayerSessionData();
      
      if (import.meta.env.DEV) {
        console.log('[TeacherSessionRedirect] Auth session detected, user is teacher. Redirecting away from player route:', location.pathname);
      }

      try {
        // Check if teacher has a school
        const { data, error } = await supabase
          .from('users')
          .select('school_id')
          .eq('id', user.id)
          .single();

        if (error) {
          console.error('[TeacherSessionRedirect] Error fetching user:', error);
          // On error, redirect to join-school as safe default
          navigate('/join-school', { replace: true });
          return;
        }

        if (!data?.school_id) {
          // No school - redirect to join-school
          if (import.meta.env.DEV) {
            console.log('[TeacherSessionRedirect] Teacher has no school, redirecting to /join-school');
          }
          navigate('/join-school', { replace: true });
        } else {
          // Has school - redirect to teacher dashboard
          if (import.meta.env.DEV) {
            console.log('[TeacherSessionRedirect] Teacher has school, redirecting to /opettaja');
          }
          navigate('/opettaja', { replace: true });
        }
      } catch (err) {
        console.error('[TeacherSessionRedirect] Unexpected error:', err);
        navigate('/join-school', { replace: true });
      } finally {
        setIsChecking(false);
      }
    };

    checkAndRedirect();
  }, [user, authLoading, navigate, location.pathname]);

  // Show loading while checking
  if (authLoading || isChecking) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  // If we should render children (player access), do so
  if (shouldRenderChildren) {
    return <>{children}</>;
  }

  // Otherwise, redirect is happening
  return null;
};

export default TeacherSessionRedirect;
